import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { TFunction } from "i18next";
import { NS } from "../../../U3148";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000CheckBoxBase from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import U2000PasswordProgressionPopup from "../../../../u2000Core/components/popup/U2000PasswordProgressionPopup";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { useEffect, useState } from "react";
import U2000BasicDialog from "../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import useU2000GoogleSsoStatus from "../../../../u2000Core/hooks/authentification/U2000UseGoogleSsoStatus";
import { U2000FormatDateTime } from "../../../../u2000Core/tools/U2000DateTimeHelpers";
import type { U3148Affiliation, U3148User } from "../../../U3148Service";

interface U3148AffiliationComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

function U3148AffiliationBody<T extends Record<string, any>>({ formStateHandlers, t }: U3148AffiliationComponentProps<T>) {
  const resetPwd = "resetPwd" in formStateHandlers.getCustomAttributes();
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [ssoConfirmOpen, setSsoConfirmOpen] = useState(false);
  const [googleSsoAvailable, setGoogleSsoAvailable] = useState<boolean | null>(null);
  const doGetGoogleSsoStatus = useU2000GoogleSsoStatus();

  const data = formStateHandlers.getData() as U3148Affiliation;
  const oldData = formStateHandlers.getOldData() as U3148Affiliation | null;
  const editMode = formStateHandlers.getFormEditMode();
  const user = data?.user;
  const ssoEnabled = Boolean(user?.ssoEnabled);
  const previousSsoEnabled = Boolean(oldData?.user?.ssoEnabled);
  const disablingSso = editMode === U2000FormStateEnumEditMode.EDIT && previousSsoEnabled && !ssoEnabled;

  const showPasswordField =
    ((editMode === U2000FormStateEnumEditMode.ADD || resetPwd) && !ssoEnabled) || disablingSso;

  useEffect(() => {
    let cancelled = false;
    doGetGoogleSsoStatus()
      .then((result) => {
        if (!cancelled) {
          setGoogleSsoAvailable(result.googleSsoAvailable === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleSsoAvailable(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chargement unique au montage
  }, []);

  const applySsoEnabled = (checked: boolean) => {
    const currentUser = (user ?? ({} as U3148User));
    formStateHandlers.setData({
      ...data,
      user: {
        ...currentUser,
        ssoEnabled: checked,
        password: checked ? "" : currentUser.password,
        unlinkGoogleIdentity: !checked && previousSsoEnabled ? true : false,
      },
    } as T);
  };

  const handleSsoCheckChange = (checked: boolean) => {
    if (checked && !ssoEnabled) {
      setSsoConfirmOpen(true);
      return;
    }
    applySsoEnabled(checked);
  };

  const googleLinkedAt = user?.googleLinkedAt;
  const googleLinkedLabel = googleLinkedAt
    ? t("user.lblGoogleLinkedAt", { ns: NS, date: U2000FormatDateTime(googleLinkedAt) })
    : t("user.lblGoogleNotLinked", { ns: NS });

  return (
    <div className="flex h-full items-stretch gap-4">
      <div className="flex flex-[66_66_0%] flex-col gap-4">
        <U2000Panel title={t("user.lblGeneralInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText
                fieldName="user.username"
                label={t("user.lblUsername", { ns: NS })}
                formStateHandlers={formStateHandlers}
                readonly={formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
              />
            </div>
            <div className="flex-1">
              {showPasswordField && (
                <div className="relative">
                  <U2000InputText
                    fieldName="user.password"
                    label={
                      disablingSso
                        ? t("user.lblPasswordOnSsoDisable", { ns: NS })
                        : t("user.lblPassword", { ns: NS })
                    }
                    formStateHandlers={formStateHandlers}
                    onFocus={() => setShowPasswordPopup(true)}
                    onBlur={() => setShowPasswordPopup(false)}
                  />
                  <U2000PasswordProgressionPopup password={user?.password || ""} show={showPasswordPopup} position="BOTTOM_CENTER" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="user.firstName" label={t("user.lblFirstName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
            <div className="flex-1">
              <U2000InputText fieldName="user.lastName" label={t("user.lblLastName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="user.email" label={t("user.lblEmail", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
        </U2000Panel>
      </div>

      <div className="flex flex-[34_34_0%]">
        <U2000Panel title={t("user.lblConnectInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="user.active" label={t("user.lblChkActive", { ns: NS })} readonly={resetPwd} />
            </div>
            <div className="flex-2">
              <U2000InputText fieldName="user.lastLogin" label={t("user.lblLastLogin", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="user.locked" label={t("user.lblChkLocked", { ns: NS })} />
            </div>
            <div className="flex-2">
              <U2000InputText type="number" fieldName="nbOfFailedAttempts" label={t("user.lblNbOfFailedAttempts", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox
                formStateHandlers={formStateHandlers}
                fieldName="user.changePasswordOnLogin"
                label={t("user.lblChkChangePasswordOnLogin", { ns: NS })}
                readonly={ssoEnabled || resetPwd}
              />
            </div>
          </div>

          <div className="mt-4 w-full border-t border-gray-200 px-2 pt-4">
            {googleSsoAvailable === false && (
              <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t("user.msgGoogleSsoUnavailable", { ns: NS })}
              </div>
            )}
            <U2000CheckBoxBase
              fieldName="user.ssoEnabled"
              label={t("user.lblChkSsoEnabled", { ns: NS })}
              value={ssoEnabled}
              setValue={handleSsoCheckChange}
              readonly={resetPwd || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
            />
            {editMode === U2000FormStateEnumEditMode.EDIT && (
              <div className="mt-2 text-sm text-gray-600">{googleLinkedLabel}</div>
            )}
            {disablingSso && (
              <div className="mt-2 text-sm text-amber-800">{t("user.msgSsoDisablePasswordRequired", { ns: NS })}</div>
            )}
          </div>
        </U2000Panel>
      </div>

      <U2000BasicDialog
        isOpen={ssoConfirmOpen}
        onClose={() => setSsoConfirmOpen(false)}
        title={t("user.dlgSsoEnableTitle", { ns: NS })}
        width="small"
      >
        <div className="flex flex-col gap-4 px-2 pb-2">
          <p className="text-sm text-gray-700">{t("user.dlgSsoEnableBody", { ns: NS })}</p>
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <U2000ActionBtn
              label={t("btnCancel", { ns: "common" })}
              variant="secondary"
              handleClick={() => setSsoConfirmOpen(false)}
            />
            <U2000ActionBtn
              label={t("user.dlgSsoEnableConfirm", { ns: NS })}
              handleClick={() => {
                applySsoEnabled(true);
                setSsoConfirmOpen(false);
              }}
            />
          </div>
        </div>
      </U2000BasicDialog>
    </div>
  );
}

export default U3148AffiliationBody;
